import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { api } from '../lib/api';
import { logger } from '../lib/terminalLogger';
import { useAuthReady } from './AuthContext';

// ============================================================================
// INTERFACES
// ============================================================================

export interface ProdutoCategoria {
  id: number;
  prod_tipo_descricao: string;
}

export interface Produto {
  pro_id: number;
  pro_nome: string;
  pro_und_id?: number;
  umd_descricao?: string;
  categoria?: number;
}

interface ProdutoCategoriasContextData {
  categorias: ProdutoCategoria[];
  produtos: Produto[];
  loading: boolean;
  error: string | null;
  refetch: (force?: boolean) => Promise<void>;
}

interface ProdutoCategoriasProviderProps {
  children: ReactNode;
}

// ============================================================================
// CONSTANTES
// ============================================================================

const CACHE_KEY = 'produto-categorias-unified-cache';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hora

// ============================================================================
// CONTEXT
// ============================================================================

const ProdutoCategoriasContext = createContext<ProdutoCategoriasContextData | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

export function ProdutoCategoriasProvider({ children }: ProdutoCategoriasProviderProps) {
  const [categorias, setCategorias] = useState<ProdutoCategoria[]>([]);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Ref para evitar múltiplas chamadas simultâneas
  const fetchingRef = useRef(false);

  /**
   * Carrega dados do cache localStorage
   */
  const loadFromCache = useCallback((): { categorias: ProdutoCategoria[]; produtos: Produto[]; valid: boolean } | null => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;

      const cacheData = JSON.parse(cached);
      const age = Date.now() - cacheData.timestamp;
      const valid = age < CACHE_TTL_MS;

      if (Array.isArray(cacheData.categorias) && Array.isArray(cacheData.produtos)) {
        return {
          categorias: cacheData.categorias,
          produtos: cacheData.produtos,
          valid
        };
      }
    } catch (err) {
      logger.warn('[ProdutoCategoriasContext] Erro ao ler cache:', err);
    }
    return null;
  }, []);

  /**
   * Salva dados no cache localStorage
   */
  const saveToCache = useCallback((data: { categorias: ProdutoCategoria[]; produtos: Produto[] }) => {
    try {
      const cacheData = {
        ...data,
        timestamp: Date.now()
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (err) {
      logger.warn('[ProdutoCategoriasContext] Erro ao salvar cache:', err);
    }
  }, []);

  /**
   * Busca dados da API - UMA ÚNICA CHAMADA
   * Endpoint: /produto-categoria-item?mode=cdr&include=produtos,outros
   */
  const fetchData = useCallback(async (force = false) => {
    // Evita chamadas duplicadas
    if (fetchingRef.current) {
      logger.debug('[ProdutoCategoriasContext] Fetch já em andamento, ignorando');
      return;
    }

    // Verificar cache primeiro (se não for force)
    if (!force) {
      const cached = loadFromCache();
      if (cached?.valid) {
        logger.debug('[ProdutoCategoriasContext] Usando dados do cache (válido)');
        setCategorias(cached.categorias);
        setProdutos(cached.produtos);
        setLoading(false);
        return;
      }
      // Se cache existe mas expirou, usar como fallback enquanto busca
      if (cached) {
        setCategorias(cached.categorias);
        setProdutos(cached.produtos);
      }
    }

    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      logger.debug('[ProdutoCategoriasContext] Buscando dados da API (chamada única)');

      // UMA ÚNICA CHAMADA que retorna tudo
      const response = await api('/produto-categoria-item?mode=cdr&include=produtos,outros');
      const data = response as {
        success?: boolean;
        data?: Array<{
          id: number;
          prod_tipo_descricao: string;
          produtos?: Array<{
            pro_id: number;
            pro_nome: string;
            pro_und_id?: number;
            umd_descricao?: string;
          }>;
          one1_outros?: Array<{
            id: number;
            one1_nome: string;
          }>;
        }>;
      };

      if (!data.success || !Array.isArray(data.data)) {
        throw new Error('Formato de resposta inválido');
      }

      // Extrair categorias (direto do array)
      const categoriasExtraidas: ProdutoCategoria[] = data.data.map(item => ({
        id: item.id,
        prod_tipo_descricao: item.prod_tipo_descricao
      }));

      // Extrair produtos (flatten dos arrays aninhados)
      const produtosExtraidos: Produto[] = [];
      const produtosMap = new Map<number, Produto>(); // Para deduplicar

      for (const categoria of data.data) {
        if (categoria.produtos && Array.isArray(categoria.produtos)) {
          for (const p of categoria.produtos) {
            if (p.pro_id && !produtosMap.has(p.pro_id)) {
              const produto: Produto = {
                pro_id: p.pro_id,
                pro_nome: p.pro_nome || `Produto ${p.pro_id}`,
                pro_und_id: p.pro_und_id,
                umd_descricao: p.umd_descricao,
                categoria: categoria.id
              };
              produtosMap.set(p.pro_id, produto);
              produtosExtraidos.push(produto);
            }
          }
        }
      }

      // Ordenar produtos alfabeticamente
      produtosExtraidos.sort((a, b) => a.pro_nome.localeCompare(b.pro_nome));

      logger.debug('[ProdutoCategoriasContext] Dados extraídos:', {
        categorias: categoriasExtraidas.length,
        produtos: produtosExtraidos.length
      });

      // Atualizar estado
      setCategorias(categoriasExtraidas);
      setProdutos(produtosExtraidos);

      // Salvar no cache
      saveToCache({
        categorias: categoriasExtraidas,
        produtos: produtosExtraidos
      });

    } catch (err) {
      logger.error('[ProdutoCategoriasContext] Erro ao buscar dados:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');

      // Tentar usar cache como fallback (mesmo expirado)
      const cached = loadFromCache();
      if (cached) {
        logger.debug('[ProdutoCategoriasContext] Usando cache expirado como fallback');
        setCategorias(cached.categorias);
        setProdutos(cached.produtos);
        setError(null); // Limpar erro se fallback funcionar
      } else {
        // Fallback final: categorias hardcoded
        setCategorias([
          { id: 1, prod_tipo_descricao: 'IMPRESSÃO DTF TÊXTIL' },
          { id: 2, prod_tipo_descricao: 'IMPRESSÃO DTF UV' },
          { id: 3, prod_tipo_descricao: 'IMPRESSÃO SUBLIMÁTICA' },
          { id: 4, prod_tipo_descricao: 'CAMISETA' }
        ]);
      }
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [loadFromCache, saveToCache]);

  // Buscar dados ao montar — somente quando a sessão estiver pronta (evita 401 na
  // tela de login e no cold-start). authReady é reativo: quando o token propaga do
  // bootstrap/refresh, o efeito re-dispara e busca já autenticado.
  const authReady = useAuthReady();
  useEffect(() => {
    if (!authReady) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [authReady, fetchData]);

  const value: ProdutoCategoriasContextData = {
    categorias,
    produtos,
    loading,
    error,
    refetch: fetchData
  };

  return (
    <ProdutoCategoriasContext.Provider value={value}>
      {children}
    </ProdutoCategoriasContext.Provider>
  );
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook principal para acessar o contexto completo
 */
export function useProdutoCategoriasContext(): ProdutoCategoriasContextData {
  const context = useContext(ProdutoCategoriasContext);
  if (!context) {
    throw new Error('useProdutoCategoriasContext must be used within a ProdutoCategoriasProvider');
  }
  return context;
}

/**
 * Hook de compatibilidade: retorna apenas categorias
 * Substitui o antigo useProdutoCategorias
 */
export function useProdutoCategorias(): {
  categorias: ProdutoCategoria[];
  loading: boolean;
  error: string | null;
} {
  const { categorias, loading, error } = useProdutoCategoriasContext();
  return { categorias, loading, error };
}

/**
 * Hook de compatibilidade: retorna apenas produtos
 * Substitui o antigo useProdutos
 */
export function useProdutos(): {
  produtos: Produto[];
  loading: boolean;
  error: string | null;
} {
  const { produtos, loading, error } = useProdutoCategoriasContext();
  return { produtos, loading, error };
}
